/**
 * The finding — sibyl's single unit of output.
 *
 * Nine fields, taken from reviews this team has actually accepted, not invented.
 * See docs/ARCHITECTURE.md, "Confidence components".
 *
 * ⛔ CONFIDENCE IS NOT A FIELD ON THIS TYPE, AND THAT IS DELIBERATE.
 * A model is never asked how sure it is. `Components` below is computed from artifacts we already
 * hold, every entry a boolean, so a person who distrusts the verdict can recompute it and point at
 * the component they disagree with. Measured: model self-scoring reaches AUROC 0.60-0.63 against a
 * TF-IDF baseline of 0.83-0.95, and over 78% of self-reported scores land on three round numbers.
 * A competing tool was abandoned after three pull requests because it attached confidence 4/5 to
 * findings that were wrong.
 */

export const SEVERITIES = ["high", "medium", "low"] as const
export type Severity = (typeof SEVERITIES)[number]

/**
 * `pre_existing` is a CLASS, not a severity — a finding can be HIGH and pre-existing at once.
 * It is computed from the diff hunks (does the cited line fall inside them), never judged, so it
 * cannot be argued with. It is the largest single category of noise in every tool surveyed.
 */
export const FINDING_CLASSES = ["introduced", "pre_existing", "shadow"] as const
export type FindingClass = (typeof FINDING_CLASSES)[number]

export const EFFORTS = ["S", "M", "L"] as const
export type Effort = (typeof EFFORTS)[number]

/** Where a finding points. `side` follows GitHub: RIGHT is the post-image, LEFT the pre-image. */
export interface Anchor {
  readonly path: string
  readonly line: number
  readonly side: "LEFT" | "RIGHT"
  /** Set only for a multi-line anchor; GitHub requires both ends or neither. */
  readonly startLine?: number
  readonly startSide?: "LEFT" | "RIGHT"
}

/**
 * The mechanical checks that decide whether a finding is believable.
 *
 * ⛔ The first three are HARD GATES. Fail any one and the finding never reaches the queue — dropped
 * without argument, never ranked lower. The rest order what survives.
 */
export interface Components {
  /** GATE: the path and line resolve in the checked-out tree at the reviewed sha. */
  readonly citedLineExists: boolean
  /** GATE: the quoted text is byte-identical to that line. Catches a paraphrased "quote". */
  readonly quoteIsByteIdentical: boolean
  /** GATE: the cited line falls inside the pull request's changed hunks. False ⇒ pre_existing. */
  readonly introducedHere: boolean
  /** The refuter was told to disprove this and could not. */
  readonly survivedRefutation: boolean
  /** Concrete inputs AND a stated wrong outcome are both present. */
  readonly hasFailureScenario: boolean
  /** The quoted rule text was found in the source file the finding names. */
  readonly ruleCitationChecksOut: boolean
}

export const HARD_GATES = [
  "citedLineExists",
  "quoteIsByteIdentical",
  "introducedHere",
] as const satisfies readonly (keyof Components)[]

/** A ```suggestion``` patch. Only produced when the whole replacement range is known. */
export interface Suggestion {
  /**
   * Every replacement line, in order.
   *
   * ⚠️ GitHub replaces the commented range IN FULL with this block. A block missing a line silently
   * deletes code, so this is never a partial edit.
   */
  readonly replacementLines: readonly string[]
  /** Inclusive line range in the post-image that this replaces. */
  readonly fromLine: number
  readonly toLine: number
}

export interface Finding {
  /**
   * Stable across rebases. Derived from the normalised dimension id plus a hash of the surrounding
   * code — NEVER from (path, line), which move under every rebase.
   *
   * ⭐ No surveyed product documents this, and it is where a triage queue lives or dies: a dismissal
   * that does not survive a force-push means the tool re-asks a settled question.
   */
  readonly fingerprint: string
  readonly dimensionId: string
  readonly severity: Severity
  readonly cls: FindingClass
  /** One line. The subject is the capability, never an identifier. */
  readonly title: string
  readonly anchor: Anchor
  /** Byte-identical to the source at `anchor`. Verified, not trusted. */
  readonly quote: string
  /** What a person experiences. No function name, table or flag as the subject of the sentence. */
  readonly consequence: string
  /** Concrete inputs and the wrong outcome they produce. */
  readonly failureScenario: string
  readonly recommendedFix: string
  readonly effort: Effort
  readonly suggestion?: Suggestion
  /**
   * The model's own reasoning, captured because the prompt demands reasoning BEFORE the finding and
   * severity LAST. A model that states a verdict first rationalises toward it.
   */
  readonly reasoning: string
  readonly components: Components
  /** Which backend and model produced it. Provenance survives into the triage queue. */
  readonly provenance: Provenance
}

export interface Provenance {
  readonly backend: "claude_cli" | "anthropic_api"
  readonly model: string
  readonly effort: string
  readonly reviewedSha: string
  /** True when the merge ref was unborn and we fell back to the head ref. Changes what a finding means. */
  readonly usedFallbackRef: boolean
}

/** Aligned to GitHub code-scanning dismissal reasons so the vocabulary transfers. */
export const DISMISS_REASONS = [
  "false_positive",
  "wont_fix",
  "used_in_tests",
  "mitigated",
] as const
export type DismissReason = (typeof DISMISS_REASONS)[number]

/** TODO is the default and is what makes remaining work visible in the queue. */
export type TriageState =
  | { readonly kind: "todo" }
  | { readonly kind: "accepted"; readonly at: string; readonly by: string }
  | {
      readonly kind: "dismissed"
      readonly at: string
      readonly by: string
      readonly reason: DismissReason
      readonly note?: string
    }

/** True when every hard gate passed. A finding failing this never reaches a human. */
export function passesHardGates(c: Components): boolean {
  return HARD_GATES.every((k) => c[k])
}

/** How many non-gate components held. Orders the queue; never shown to the pull-request author. */
export function componentScore(c: Components): number {
  return [c.survivedRefutation, c.hasFailureScenario, c.ruleCitationChecksOut].filter(Boolean).length
}
